import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { CBadge, CNavTitle, CNavItem } from '@coreui/react-pro';
import { CaretDownIcon } from '@phosphor-icons/react';

export const AppSidebarNav = ({ items }) => {
  const location = useLocation();
  const [expandedGroups, setExpandedGroups] = useState({});

  // Function to render navTitle
  const navTitle = (name) => {
    return (
      <div className='sidebar-nav-title'>
        {name}
      </div>
    );
  };

  // Function to render navItem
  const navItem = (item, index) => {
    const { component, name, badge, icon, show, classNames, ...rest } = item;
    const Component = component;

    if (Component === CNavTitle) {
      return (
        <div className='sidebar-nav-title-wrapper' key={index}>
          {navTitle(name)}
        </div>
      );
    }

    if (Component === CNavItem) {
      return (
        <NavLink
          to={rest.to || '#'}
          className={({ isActive }) => `sidebar-nav-link align-menu-items ${classNames || ''} ${isActive ? 'active' : ''}`}
          key={index}
          title={name}
        >
          {icon && React.cloneElement(icon, { className: 'nav-icon' })}
          <span className="nav-text">{name}</span>
          {badge && (
            <CBadge color={badge.color} className='ms-auto nav-badge'>
              {badge.text}
            </CBadge>
          )}
        </NavLink>
      );
    }

    return null;
  };

  // Function to render navGroup
  const navGroup = (item, index) => {
    const { component, name, icon, to, show, disabled, ...rest } = item;

    // Check if any child item matches current location
    const hasActiveChild = (items) => {
      if (!items) return false;
      return items.some((child) => {
        if (child.to && (location.pathname === child.to || location.pathname.startsWith(child.to))) return true;
        if (child.items) return hasActiveChild(child.items);
        return false;
      });
    };

    const isChildActive = hasActiveChild(item.items);
    const isCurrentlyExpanded = expandedGroups[to] !== undefined
      ? expandedGroups[to]
      : (isChildActive || location.pathname.startsWith(to) || (to === '/email/templates' && location.pathname.startsWith('/campaigns')));

    const handleToggle = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (disabled) return;
      setExpandedGroups((prev) => ({
        ...prev,
        [to]: !isCurrentlyExpanded,
      }));
    };

    return (
      <div className='nav-group-container' key={index}>
        <div
          className={`sidebar-nav-link nav-group-parent ${isCurrentlyExpanded ? 'group-expanded' : ''} ${isChildActive ? 'child-active' : ''}`}
          onClick={handleToggle}
          title={name}
          style={disabled ? { opacity: 0.45, cursor: 'not-allowed', pointerEvents: 'none' } : undefined}
          role="button"
          tabIndex={0}
        >
          {icon && React.cloneElement(icon, { className: 'nav-icon' })}
          <span className="nav-text">{name}</span>
          <CaretDownIcon
            weight="bold"
            size={12}
            className={`nav-group-chevron ms-auto ${isCurrentlyExpanded ? 'rotated' : ''}`}
          />
        </div>
        {isCurrentlyExpanded && (
          <div className='sidebar-nav-group'>
            {item.items?.filter((item) => item.show).map((item, idx) =>
              item.items ? navGroup(item, idx) : navItem(item, idx)
            )}
          </div>
        )}
      </div>
    );
  };

  // Render the sidebar navigation
  return (
    <div className="sidenav-wrapper">
      {items &&
        items.filter((item) => item.show).map((item, index) =>
          item.items ? navGroup(item, index) : navItem(item, index)
        )}
    </div>
  );
};

AppSidebarNav.propTypes = {
  items: PropTypes.arrayOf(PropTypes.any).isRequired,
};
